package koala.dom

import js.array.asList
import koala.markdown.*
import koala.model.EditorStyle
import koala.modifier.*
import web.html.HTMLBRElement
import web.html.HTMLElement
import kotlin.collections.forEach
import kotlin.sequences.forEach
import kotlin.sequences.plus

/** A range of a markdown chunk and the class the editor styles it with. */
data class EditorSegment(val from: Int, val to: Int, val mod: Class)

/** The styled ranges of this block's text, covering it end to end. */
fun ParsedBlock.toEditorSegments(): List<EditorSegment> = segmentsIn(chunk, 0, chunk.length, markdown.editorSpans())

/** The styled ranges of [chunk] from [from] to [to], from its markdown [spans], with the text between them as extra. */
fun segmentsIn(chunk: String, from: Int, to: Int, spans: Sequence<MarkdownSpan>): List<EditorSegment> = buildList {
    var index = from

    spans.forEach { span ->
        addExtra(chunk, index, span.index)
        index = span.index

        val contentEnd = index + span.text.length
        if (contentEnd > index) {
            add(EditorSegment(index, contentEnd, span.contentMod))
        }
        index = contentEnd

        if (span is MarkdownUrl) {
            addExtra(chunk, index, span.urlIndex)
            val urlEnd = span.urlIndex + span.url.value.length
            add(EditorSegment(span.urlIndex, urlEnd, EditorStyle.Url))
            index = urlEnd
        }
    }

    addExtra(chunk, index, to)
}

private fun MutableList<EditorSegment>.addExtra(chunk: String, from: Int, to: Int) {
    var start = from
    while (start < to) {
        val newline = chunk.indexOf('\n', start)
        if (newline == -1 || newline >= to) {
            add(EditorSegment(start, to, EditorStyle.Extra))
            return
        }
        if (newline > start) {
            add(EditorSegment(start, newline, EditorStyle.Extra))
        }
        var end = newline
        while (end < to && chunk[end] == '\n') end++
        add(EditorSegment(newline, end, EditorStyle.Space))
        start = end
    }
}

private fun MarkdownBlock.editorSpans(): Sequence<MarkdownSpan> = when (this) {
    is MarkdownParagraph -> spans.asSequence()
    is MarkdownHeading -> spans.asSequence()
    is MarkdownBlockquote -> paragraphs.asSequence().flatMap { it.spans }
    is MarkdownList -> items.asSequence().flatMap { it.editorSpans() }
    is MarkdownTable -> (sequenceOf(header) + rows).flatMap { row ->
        row.cells.asSequence().flatMap { it.spans }
    }
    is MarkdownCodeBlock -> when {
        code.isEmpty() -> emptySequence()
        else -> sequenceOf(MarkdownInlineCode(code, codeIndex))
    }
    is MarkdownBlockImage -> sequenceOf(
        MarkdownInlineImage(altText, altTextIndex, url, urlIndex, maxWidthPercent, type)
    )
    MarkdownHorizontalRule -> emptySequence()
}

private fun MarkdownListItem.editorSpans(): Sequence<MarkdownSpan> =
    spans.asSequence() + (sublist?.editorSpans() ?: emptySequence())


/** True when this element's spans already render [segments] of [chunk], so it need not be rebuilt. */
fun HTMLElement.matchesEditorSegments(chunk: String, segments: List<EditorSegment>?): Boolean {
    if (segments == null) return false

    val nodes = childNodes.asList()
    var nodeIndex = 0

    segments.forEach { segment ->
        val node = nodes.getOrNull(nodeIndex++) as? HTMLElement ?: return false
        if (node.tagName != "SPAN") return false
        if (!node.isModified(segment.mod)) return false

        val text = node.textContent ?: return false
        if (text.length != segment.to - segment.from) return false
        if (!chunk.regionMatches(segment.from, text, 0, text.length)) return false
    }

    if (chunk.isEmpty() || chunk.endsWith("\n")) {
        if (nodes.getOrNull(nodeIndex++) !is HTMLBRElement) return false
    }

    return nodeIndex == nodes.size
}

private val MarkdownSpan.contentMod get() = when (this) {
    is MarkdownStrong -> EditorStyle.Strong
    is MarkdownEmphasis -> EditorStyle.Emphasis
    is MarkdownInlineCode -> EditorStyle.InlineCode
    is MarkdownStrikethrough -> EditorStyle.Strikethrough
    is MarkdownLink, is MarkdownInlineImage -> EditorStyle.LinkText
    is MarkdownText -> EditorStyle.Text
}

